import type { ContextoEuResposta } from "@zaphub/types";
import { create } from "zustand";

/**
 * Sessão do painel. O access token vive SÓ em memória (nunca localStorage): o
 * refresh token é um cookie httpOnly que o navegador envia ao /auth/refresh, e é
 * assim que a sessão é restaurada ao recarregar a página.
 */
export interface UsuarioSessao {
  id: string;
  nome: string;
  email: string;
  role: "OWNER" | "SUPERADMIN";
  /** true → o painel fica bloqueado até a troca de senha (senha provisória). */
  deveTrocarSenha?: boolean;
}

export interface ContaSessao {
  id: string;
  nome: string;
  status: "trialing" | "active" | "past_due" | "suspended" | "canceled";
  assinaturaAtiva: boolean;
  trialEndsAt: string | null;
  plano: { id?: string; nome: string; limiteNumeros: number; precoCentavos?: number } | null;
  numerosUsados: number;
}

export interface EstadoSessao {
  accessToken: string | null;
  usuario: UsuarioSessao | null;
  conta: ContaSessao | null;
  /** false até a tentativa inicial de restaurar a sessão terminar. */
  iniciada: boolean;
  /** SUPERADMIN acessando como cliente: id do SUPERADMIN. */
  impersonadoPor: string | null;
  definirToken: (token: string | null) => void;
  definirSessao: (s: { usuario: UsuarioSessao | null; conta: ContaSessao | null; impersonadoPor?: string | null }) => void;
  marcarIniciada: () => void;
  limpar: () => void;
}

export const useSessao = create<EstadoSessao>((set) => ({
  accessToken: null,
  usuario: null,
  conta: null,
  iniciada: false,
  impersonadoPor: null,
  definirToken: (accessToken) => set({ accessToken }),
  definirSessao: ({ usuario, conta, impersonadoPor = null }) => set({ usuario, conta, impersonadoPor }),
  marcarIniciada: () => set({ iniciada: true }),
  limpar: () => set({ accessToken: null, usuario: null, conta: null, impersonadoPor: null }),
}));

/**
 * `GET /auth/eu` → formato da sessão do painel. O contrato real separa `conta`, `plano`,
 * `numeros` e `assinaturaAtiva`; o painel os junta em `ContaSessao` para o resto do código.
 */
export function contextoParaSessao(eu: ContextoEuResposta): {
  usuario: UsuarioSessao;
  conta: ContaSessao | null;
  impersonadoPor: string | null;
} {
  const conta: ContaSessao | null = eu.conta
    ? {
        id: eu.conta.id,
        nome: eu.conta.nome,
        status: eu.conta.status,
        assinaturaAtiva: eu.assinaturaAtiva,
        trialEndsAt: eu.conta.trialEndsAt,
        plano: eu.plano
          ? {
              id: eu.plano.id,
              nome: eu.plano.nome,
              limiteNumeros: eu.plano.limiteNumeros,
              precoCentavos: eu.plano.precoCentavos,
            }
          : null,
        numerosUsados: eu.numeros?.usados ?? 0,
      }
    : null;
  return { usuario: eu.usuario, conta, impersonadoPor: eu.impersonadoPor ?? null };
}
